export interface Forecast {
  id: string
  label: string
  detail: string
  horizon: string
  confidence: number
  tone: 'positive' | 'watch' | 'risk'
}

export interface Anomaly {
  id: string
  ref: string
  subject: string
  signal: string
  severity: 'critical' | 'high' | 'medium'
  confidence: number
  detectedOn: string
  recommendation: string
}

export const forecasts: Forecast[] = [
  {
    id: 'fc-1',
    label: 'FIFA 2038 bid clears its gate by 12 September',
    detail:
      'Requirement coverage is projected to reach 87% once the federal tax exemption guarantee is issued. Escalated guarantees have cleared drafting in a median 19 days across 14 comparable cases.',
    horizon: 'Next 30 days',
    confidence: 84,
    tone: 'watch'
  },
  {
    id: 'fc-2',
    label: 'Asian Games 2034 Readiness gate slips 3–5 weeks',
    detail:
      'Workforce domain is trending +2.1 points per week against an 11-point gap. Even with the September rehearsal, the domain reaches threshold after the scheduled 15 October gate review.',
    horizon: 'Next 90 days',
    confidence: 79,
    tone: 'risk'
  },
  {
    id: 'fc-3',
    label: 'Portfolio support commitment reaches AED 19.4B by Q1 2027',
    detail:
      'Assuming current advancement rates and the two Assessment records clearing on revised funding structures, committed support rises 24% against the FY2026 baseline.',
    horizon: 'Next 2 quarters',
    confidence: 72,
    tone: 'positive'
  },
  {
    id: 'fc-4',
    label: 'Two Assessment records will require a second information cycle',
    detail:
      'Co-funding evidence has been outstanding beyond 20 days on both records. Historically 68% of such records need a second request before the gate can be assessed.',
    horizon: 'Next 45 days',
    confidence: 76,
    tone: 'watch'
  }
]

export const anomalies: Anomaly[] = [
  {
    id: 'an-1',
    ref: 'SDU-MAJ-2025-041',
    subject: 'COP38 — budget draw-down velocity',
    signal:
      'Draw-down is 1.46x schedule completion, outside the 1.25x tolerance band for Major events and 2.3 standard deviations above the portfolio mean.',
    severity: 'high',
    confidence: 92,
    detectedOn: '2026-08-11',
    recommendation: 'Commission an independent cost-to-complete review before the Plan & Build gate.'
  },
  {
    id: 'an-2',
    ref: 'SDU-MEG-2024-008',
    subject: 'Asian Games 2034 — volunteer attrition cluster',
    signal:
      'Attrition is concentrated in a single function (venue services) at 4.1x the rate of all other functions — a pattern seen in two prior Games records.',
    severity: 'critical',
    confidence: 88,
    detectedOn: '2026-08-09',
    recommendation: 'Second a workforce specialist and hold the gate until the rehearsal result is known.'
  },
  {
    id: 'an-3',
    ref: 'SDU-INT-2026-097',
    subject: 'Ajman Heritage Days — channel under-delivery',
    signal:
      'Tourism board placements at 58% of guarantee while portal placements over-deliver by 22% — a reallocation signature seen on 31 prior campaigns.',
    severity: 'medium',
    confidence: 81,
    detectedOn: '2026-08-14',
    recommendation: 'Reallocate two portal slots to recover the guarantee within three weeks.'
  },
  {
    id: 'an-4',
    ref: 'SDU-MEG-2026-036',
    subject: 'Military World Games 2033 — shared venue dependency',
    signal:
      'Fifteen venues are shared with a record currently holding at Readiness, creating a correlated delivery risk not priced into either baseline.',
    severity: 'high',
    confidence: 77,
    detectedOn: '2026-08-13',
    recommendation: 'Sequence the venue plan against the Asian Games 2034 readiness gate.'
  },
  {
    id: 'an-5',
    ref: 'SDU-MAJ-2026-029',
    subject: 'Global Space Congress 2028 — stalled information request',
    signal:
      'Information request open 19 days with no entity response; 68% of such records need a second cycle, adding a median 24 days to the stage.',
    severity: 'medium',
    confidence: 85,
    detectedOn: '2026-08-15',
    recommendation: 'Escalate to the entity accountable officer before the SLA lapses.'
  }
]

export const gatePerformance = [
  { month: 'Feb', predicted: 4, actual: 4, overrides: 0 },
  { month: 'Mar', predicted: 5, actual: 4, overrides: 1 },
  { month: 'Apr', predicted: 6, actual: 6, overrides: 1 },
  { month: 'May', predicted: 5, actual: 3, overrides: 2 },
  { month: 'Jun', predicted: 7, actual: 6, overrides: 1 },
  { month: 'Jul', predicted: 6, actual: 5, overrides: 2 },
  { month: 'Aug', predicted: 8, actual: 6, overrides: 1 }
]

export const cycleTimes = [
  { stage: 'Assessment', meanDays: 26, targetDays: 21 },
  { stage: 'Bid', meanDays: 118, targetDays: 120 },
  { stage: 'Approval', meanDays: 41, targetDays: 35 },
  { stage: 'Plan & Build', meanDays: 402, targetDays: 420 },
  { stage: 'Readiness', meanDays: 96, targetDays: 90 },
  { stage: 'Deliver & Operate', meanDays: 34, targetDays: 30 }
]

export const criterionBenchmarks = [
  { criterion: 'Strategic fit', portfolio: 87, benchmark: 80 },
  { criterion: 'Economic impact', portfolio: 78, benchmark: 75 },
  { criterion: 'Entity capability', portfolio: 81, benchmark: 78 },
  { criterion: 'Infrastructure', benchmark: 76, portfolio: 85 },
  { criterion: 'Financial viability', portfolio: 69, benchmark: 78 },
  { criterion: 'Legacy', portfolio: 80, benchmark: 74 }
]

export const recommendationImpact = [
  { stage: 'Assessment', triggered: 53, accepted: 44 },
  { stage: 'Bid', triggered: 39, accepted: 33 },
  { stage: 'Approval', triggered: 22, accepted: 18 },
  { stage: 'Plan & Build', triggered: 15, accepted: 12 },
  { stage: 'Readiness', triggered: 9, accepted: 9 },
  { stage: 'Outreach', triggered: 39, accepted: 32 }
]

export const modelVersions = [
  {
    version: 'v4.2',
    releasedOn: '2026-07-01',
    accuracy: 91,
    note: 'Added host-body requirement coverage as a predictor for bid gate slippage.'
  },
  {
    version: 'v4.1',
    releasedOn: '2026-04-14',
    accuracy: 88,
    note: 'Recalibrated economic impact weighting against Ministry of Economy assurance data.'
  },
  {
    version: 'v4.0',
    releasedOn: '2026-01-20',
    accuracy: 84,
    note: 'Introduced stage-specific criteria sets and gate readiness verdicts.'
  }
]

export const modelPerformance = {
  gateForecastAccuracy: 91,
  scoreOverrideRate: 12,
  recommendationAcceptance: 84,
  meanReviewTimeSavedHours: 6.4,
  driftNote:
    'Financial viability scoring shows mild drift against assured outcomes since June. The next recalibration is scheduled with the Ministry of Economy dataset refresh in September.'
}
